import { useState } from 'react';
import { Database, Save, AlertCircle, Film, Tv } from 'lucide-react';

export default function VodBulkImport() {
  const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';
  
  const [jsonInput, setJsonInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [importType, setImportType] = useState<'vod' | 'iptv'>('vod');
  const [result, setResult] = useState<any>(null);

  const handleImport = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      const payload = JSON.parse(jsonInput);
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
      
      const endpoint = importType === 'vod' ? '/content/bulk-import' : '/iptv/bulk-import';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Import failed');
      
      setResult(data);
      if (data.success) {
        setJsonInput(''); // clear on success
      }
    } catch (e: any) {
      setResult({ error: e.message || 'Invalid JSON format or network error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setJsonInput(event.target.result as string);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center">
          <Database className="w-6 h-6 mr-2" />
          Bulk Import JSON
        </h1>
        <p className="text-sm text-slate-500 mt-1">Upload a JSON file or paste your JSON contents here. You can paste a flat array of objects `[ {'{'} ... {'}'} ]`.</p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        
        <div className="flex gap-4 mb-6">
          <button 
            className={`flex-1 py-3 px-4 rounded-lg border flex items-center justify-center gap-2 transition-colors ${importType === 'vod' ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setImportType('vod')}
          >
            <Film size={20} />
            Movies & Series (VOD)
          </button>
          <button 
            className={`flex-1 py-3 px-4 rounded-lg border flex items-center justify-center gap-2 transition-colors ${importType === 'iptv' ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setImportType('iptv')}
          >
            <Tv size={20} />
            Live TV & Radios
          </button>
        </div>

        {result?.error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center border border-red-100">
            <AlertCircle className="w-5 h-5 mr-2" />
            {result.error}
          </div>
        )}
        
        {result?.success && (
          <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center border border-green-100">
            <Save className="w-5 h-5 mr-2" />
            Import successful! 
            {importType === 'vod' 
              ? ` Movies: ${result.importedMovies || 0}, Series: ${result.importedSeries || 0}`
              : ` Channels/Radios: ${result.importedChannels || 0}`
            }
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-700">JSON Payload</label>
          <input 
            type="file" 
            accept=".json,.txt" 
            onChange={handleFileUpload} 
            className="text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
          />
        </div>
        
        <textarea
          className="w-full h-96 font-mono text-sm p-4 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 outline-none transition-all"
          placeholder='[\n  {\n    "Title": "Movie Title",\n    "Type": "Movie",\n    ...\n  }\n]'
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          dir="ltr"
        />

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleImport}
            disabled={loading || !jsonInput.trim()}
            className="bg-slate-900 text-white px-6 py-2.5 rounded-lg flex items-center hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {loading ? 'Importing...' : 'Start Import'}
          </button>
        </div>
      </div>
    </div>
  );
}
